// Products.jsx — Plain white page, stable right-side panel, null-safe clicks
import React, { useEffect, useMemo, useRef, useState } from "react";

/* -------------------- Feature Card -------------------- */
const FeatureCard = ({
  icon, title, desc, index, id, activeId, setActiveId, onPick, tone = "green",
}) => {
  const cardRef = useRef(null);

  // tilt (null-safe)
  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const rx = ((y - r.height / 2) / r.height) * -8;
    const ry = ((x - r.width / 2) / r.width) * 8;
    el.style.setProperty("--rx", `${rx}deg`);
    el.style.setProperty("--ry", `${ry}deg`);
  };
  const onLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty("--rx", `0deg`);
    el.style.setProperty("--ry", `0deg`);
  };

  const openPanel = (e) => {
    const btn = e?.currentTarget;
    try { btn?.classList.add("press"); } catch {}
    setTimeout(() => { try { btn?.classList.remove("press"); } catch {} }, 180);
    setActiveId(id);
    onPick(id);
  };

  const isActive = activeId === id;
  const toneClass = tone === "blue" ? "tone-blue" : "tone-green";

  return (
    <article
      ref={cardRef}
      className={`pro-card reveal ${toneClass} ${isActive ? "active" : ""}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={openPanel}           // click anywhere on card
      style={{ "--i": index }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPanel(e)}
    >
      <div className="pro-row">
        {/* Icon button = 80px box / 70px emoji */}
        <button className="pro-icon" aria-label={`Open ${title}`} onClick={openPanel}>
          <span className="pro-emoji" aria-hidden="true">{icon}</span>
        </button>

        <h3 className="pro-h3">{title}</h3>
      </div>
      <p className="pro-desc">{desc}</p>

      {/* sweep highlight */}
      <span className="pro-shine" />
    </article>
  );
};

/* -------------------- Side Panel (content per feature) -------------------- */
const SidePanel = ({
  selected,
  ads, setAds,
  loading, data, kpis, onCollect,
  onAnalyze, analyzing, analysis,
  secure, setSecure,
  speed, setSpeed,
  onClose,
}) => {
  const fmt = (n) => `₹${n.toLocaleString()}`;

  return (
    <aside className={`side ${selected ? "open" : ""}`} aria-live="polite">
      <div className="side-head">
        <div className="side-title">{selected ? selected.title : "—"}</div>
        <button className="side-close" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {!selected && <p className="muted">Click any card to see details here.</p>}

      {/* Click-to-Chat Ads */}
      {selected?.id === "ads" && (
        <div className="ads-preview">
          <div className="ad-card">
            <div className="ad-media">50% Off – Festive Sale</div>
            <div className="ad-meta">
              <div>
                <div className="ad-title">Tap to chat on WhatsApp</div>
                <div className="ad-sub">Instagram / Facebook Ad</div>
              </div>
              <button
                className="btn btn-green"
                onClick={() =>
                  setAds((s) => ({
                    ...s,
                    impressions: s.impressions + 1,
                    clicks: s.clicks + 1,
                    chats: s.chats + 1,
                  }))
                }
              >
                Send Message
              </button>
            </div>
          </div>
          <div className="ad-stats">
            <div className="kpi"><div className="kpi-v">{ads.impressions}</div><div className="kpi-l">Impressions</div></div>
            <div className="kpi"><div className="kpi-v">{ads.clicks}</div><div className="kpi-l">Clicks</div></div>
            <div className="kpi"><div className="kpi-v">{ads.chats}</div><div className="kpi-l">Chats</div></div>
          </div>
          <div className="ad-metrics">
            <Metric label="CTR" value={`${pct(ads.clicks, ads.impressions)}%`} />
            <Metric label="Click→Chat CVR" value={`${pct(ads.chats, ads.clicks)}%`} />
          </div>
        </div>
      )}

      {/* AI Automation */}
      {selected?.id === "ai" && (
        <div className="ai-preview">
          <div className="ai-rail">
            <span className="dot start">Lead</span>
            <span className="pipe" />
            <span className="dot">Qualify</span>
            <span className="pipe" />
            <span className="dot">CRM</span>
            <span className="runner" />
          </div>
          <ul className="ai-bubbles">
            <li className="msg in">Hi 👋 Need a loan?</li>
            <li className="msg out">What amount?</li>
            <li className="msg in">₹1,00,000</li>
            <li className="msg out">Great. Few quick questions…</li>
          </ul>
          <p className="muted small">Automation qualifies the lead and pushes structured data to CRM.</p>
        </div>
      )}

      {/* Analytics & Insights */}
      {selected?.id === "analytics" && (
        <div className="analytics">
          <button className="btn btn-blue" onClick={onAnalyze} disabled={analyzing}>
            {analyzing ? "Analyzing…" : "Analyze sample data"}
          </button>
          {analyzing && <div className="loader loader-blue" />}

          {!analyzing && analysis && (
            <>
              <div className="kpi-row">
                <div className="kpi"><div className="kpi-v">{analysis.orders}</div><div className="kpi-l">Orders</div></div>
                <div className="kpi"><div className="kpi-v">{fmt(analysis.gmv)}</div><div className="kpi-l">GMV</div></div>
                <div className="kpi"><div className="kpi-v">{fmt(analysis.aov)}</div><div className="kpi-l">AOV</div></div>
              </div>

              <h4 className="block-title">Top Products by Revenue</h4>
              <Bars data={analysis.topProducts.map(tp => ({ label: tp.product, value: tp.revenue }))} tone="blue" />

              <h4 className="block-title">Status Mix</h4>
              <div className="status-row">
                {analysis.statusMix.map(s => (
                  <div key={s.status} className="status-cell">
                    <span className={`pill ${s.status.toLowerCase()}`}>{s.status}</span>
                    <span className="muted">{s.count} · {fmt(s.revenue)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Secure & Trusted */}
      {selected?.id === "secure" && (
        <div className="secure">
          {[
            ["Verified Business Profile", "verified"],
            ["Team 2FA enabled", "tfa"],
            ["API Keys rotated (90d)", "keys"],
            ["Webhook signatures checked", "webhooks"],
          ].map(([label, key]) => (
            <label key={key} className="check">
              <input
                type="checkbox"
                checked={!!secure[key]}
                onChange={(e) => setSecure((s) => ({ ...s, [key]: e.target.checked }))}
              />
              <span>{label}</span>
            </label>
          ))}
          <div className="secure-result">
            <strong>Posture:</strong>{" "}
            <span className={`pill ${secureScore(secure) >= 75 ? "paid" : "pending"}`}>
              {secureScore(secure)} / 100
            </span>
          </div>
        </div>
      )}

      {/* Faster Conversions */}
      {selected?.id === "speed" && (
        <div className="speed">
          <label className="muted">Avg. reply time: <strong>{speed.reply}s</strong></label>
          <input
            type="range"
            min="10"
            max="300"
            value={speed.reply}
            onChange={(e) => setSpeed((s) => ({ ...s, reply: +e.target.value }))}
          />
          <div className="meter">
            <div
              className="meter-fill"
              style={{ width: `${Math.min(100, predictConversion(speed.reply))}%` }}
            />
          </div>
          <div className="kpi-row">
            <div className="kpi"><div className="kpi-v">{predictConversion(speed.reply).toFixed(0)}%</div><div className="kpi-l">Pred. Conversion</div></div>
            <div className="kpi"><div className="kpi-v">x{(predictLift(speed.reply)).toFixed(2)}</div><div className="kpi-l">Lift vs 2 min</div></div>
          </div>
        </div>
      )}

      {/* Commerce Ready */}
      {selected?.id === "commerce" && (
        <div className="commerce">
          <div className="kpi-row">
            <div className="kpi"><div className="kpi-v">{kpis.orders}</div><div className="kpi-l">Orders</div></div>
            <div className="kpi"><div className="kpi-v">{fmt(kpis.gmv)}</div><div className="kpi-l">GMV</div></div>
            <div className="kpi"><div className="kpi-v">{fmt(kpis.aov)}</div><div className="kpi-l">AOV</div></div>
            <button className="btn btn-green" onClick={onCollect} disabled={loading}>
              {loading ? "Collecting…" : "Collect sample data"}
            </button>
          </div>

          {loading && <div className="loader" />}

          {!loading && !!data.length && (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>#</th><th>Product</th><th>Qty</th><th>Price</th><th>Status</th><th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td>{r.product}</td>
                      <td>{r.qty}</td>
                      <td>{fmt(r.price)}</td>
                      <td><span className={`pill ${r.status.toLowerCase()}`}>{r.status}</span></td>
                      <td>{r.when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

/* ---------- Small subcomponents ---------- */
const Metric = ({ label, value }) => (
  <div className="metric">
    <div className="metric-v">{value}</div>
    <div className="metric-l">{label}</div>
  </div>
);

const Bars = ({ data, tone = "green" }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  const cls = tone === "blue" ? "bar-fill blue" : "bar-fill green";
  return (
    <div className="bars">
      {data.map((d) => (
        <div key={d.label} className="bar-row">
          <div className="bar-label">{d.label}</div>
          <div className="bar-track">
            <div className={cls} style={{ width: `${(d.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

/* -------------------- Mock Data Helpers -------------------- */
const productsList = ["Running Shoes", "Wireless Buds", "Smart Watch", "Backpack", "Jacket", "Sunglasses"];
const statuses = ["Paid", "Pending", "Refunded", "COD"];
const randomInt = (a, b) => Math.floor(a + Math.random() * (b - a + 1));
const pct = (num, den) => (den ? Math.round((num / den) * 100) : 0);

function buildOrders(n = 14) {
  const rows = Array.from({ length: n }).map(() => {
    const qty = randomInt(1, 3);
    const price = randomInt(899, 4999) * qty;
    return {
      id: Math.random().toString(36).slice(2),
      product: productsList[randomInt(0, productsList.length - 1)],
      qty,
      price,
      status: statuses[randomInt(0, statuses.length - 1)],
      when: `${randomInt(1, 29)} mins ago`,
    };
  });
  return rows;
}

function analyzeOrders(rows) {
  const orders = rows.length;
  const gmv = rows.reduce((s, r) => s + r.price, 0);
  const aov = orders ? Math.round(gmv / orders) : 0;

  const byProductMap = {};
  rows.forEach(r => (byProductMap[r.product] = (byProductMap[r.product] || 0) + r.price));
  const topProducts = Object.entries(byProductMap)
    .map(([product, revenue]) => ({ product, revenue }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const byStatusMap = {};
  rows.forEach(r => {
    if (!byStatusMap[r.status]) byStatusMap[r.status] = { count: 0, revenue: 0, status: r.status };
    byStatusMap[r.status].count += 1;
    byStatusMap[r.status].revenue += r.price;
  });
  const statusMix = Object.values(byStatusMap).sort((a, b) => b.count - a.count);

  return { orders, gmv, aov, topProducts, statusMix };
}

const secureScore = (flags) => {
  const keys = ["verified","tfa","keys","webhooks"];
  const on = keys.filter(k => flags[k]).length;
  return Math.round((on / keys.length) * 100);
};

const predictConversion = (seconds) => {
  const x = Math.max(10, Math.min(300, seconds));
  const y = 65 * Math.exp(-0.0085 * (x - 10)) + 7;
  return Math.max(5, Math.min(95, y));
};
const predictLift = (seconds) => {
  const base = predictConversion(120);
  const cur = predictConversion(seconds);
  return cur / base;
};

/* -------------------- Main Section -------------------- */
const Products = () => {
  const outerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState(null);

  // Ads sim state
  const [ads, setAds] = useState({ impressions: 1200, clicks: 180, chats: 95 });

  // Commerce state (orders)
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  // Analytics state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  // Security toggles
  const [secure, setSecure] = useState({ verified: true, tfa: false, keys: true, webhooks: false });

  // Speed model
  const [speed, setSpeed] = useState({ reply: 90 });

  useEffect(() => {
    requestAnimationFrame(() => {
      if (outerRef.current) outerRef.current.classList.add("is-mounted");
    });
  }, []);

  const handlePick = (id) => {
    const metaMap = {
      ads: { id: "ads", title: "Click-to-Chat Ads" },
      ai: { id: "ai", title: "AI Automation" },
      analytics: { id: "analytics", title: "Analytics & Insights" },
      secure: { id: "secure", title: "Secure & Trusted" },
      speed: { id: "speed", title: "Faster Conversions" },
      commerce: { id: "commerce", title: "Commerce Ready" },
    };
    setSelected(metaMap[id]);
    if (id === "commerce") collectData();
    if (id === "analytics" && !orders.length) setOrders(buildOrders(14));
  };

  const collectData = () => {
    setLoading(true);
    setOrders([]);
    setTimeout(() => {
      setOrders(buildOrders(14));
      setLoading(false);
    }, 900);
  };

  const onAnalyze = () => {
    setAnalyzing(true);
    setAnalysis(null);
    const rows = orders.length ? orders : buildOrders(14);
    setTimeout(() => {
      setAnalysis(analyzeOrders(rows));
      setAnalyzing(false);
      if (!orders.length) setOrders(rows);
    }, 900);
  };

  const closePanel = () => setSelected(null);

  const kpis = useMemo(() => {
    const gmv = orders.reduce((s, r) => s + r.price, 0);
    const aov = orders.length ? Math.round(gmv / orders.length) : 0;
    return { orders: orders.length, gmv, aov };
  }, [orders]);

  return (
    <section ref={outerRef} className="pro-outer">
      <div className="pro-wrap">
        <header className="pro-head">
          <h2 className="pro-title">Product highlights</h2>
          <p className="pro-tag">Everything you need to make click-to-WhatsApp ads convert.</p>
        </header>

        {/* grid + side panel container */}
        <div className="layout">
          <div className="pro-grid">
            <FeatureCard
              id="ads" index={0} activeId={activeId} setActiveId={setActiveId} onPick={handlePick}
              title="Click-to-Chat Ads" tone="green"
              desc="Run ads on Facebook/Instagram that open a WhatsApp conversation instantly."
            />
            <FeatureCard
              id="ai" index={1} activeId={activeId} setActiveId={setActiveId} onPick={handlePick}
              title="AI Automation" tone="blue"
              desc="Handle FAQs, qualify leads, and trigger workflows with smart conversational flows."
            />
            <FeatureCard
              id="analytics" index={2} activeId={activeId} setActiveId={setActiveId} onPick={handlePick}
              title="Analytics & Insights" tone="green"
              desc="Track CTR, conversations, and conversions—optimize campaigns with data."
            />
            <FeatureCard
              id="secure" index={3} activeId={activeId} setActiveId={setActiveId} onPick={handlePick}
              title="Secure & Trusted" tone="blue"
              desc="End-to-end encrypted messaging on the WhatsApp Business Platform."
            />
            <FeatureCard
              id="speed" index={4} activeId={activeId} setActiveId={setActiveId} onPick={handlePick}
              title="Faster Conversions" tone="green"
              desc="Real-time chat reduces drop-offs and shortens the purchase journey."
            />
            <FeatureCard
              id="commerce" index={5} activeId={activeId} setActiveId={setActiveId} onPick={handlePick}
              title="Commerce Ready" tone="blue"
              desc="Share catalogs, collect orders, and send payment links inside chat."
            />
          </div>

          {/* Right, sticky side panel */}
          <SidePanel
            selected={selected}
            ads={ads} setAds={setAds}
            loading={loading}
            data={orders}
            kpis={kpis}
            onCollect={collectData}
            onAnalyze={onAnalyze}
            analyzing={analyzing}
            analysis={analysis}
            secure={secure} setSecure={setSecure}
            speed={speed} setSpeed={setSpeed}
            onClose={closePanel}
          />
        </div>
      </div>

      {/* Styles (plain white; no grid/lines/spotlight) */}
      <style>{`
        :root, html, body { background:#ffffff; }

        .pro-outer{
          --text:#0f172a; --muted:#64748b;
          --green:#1DB954; --greenDark:#169c43;
          --blue:#0ea5e9; --blueDark:#2563eb;
          --title:2.5rem; --tag:1rem;
          background:#fff; color:var(--text);
        }
        .pro-wrap{ max-width:1450px; margin:0 auto; padding:72px 60px 90px; font-family:Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
        .pro-head{ text-align:center; margin-bottom:28px; }
        .pro-title{ margin:0; font-size:var(--title); font-weight:800; }
        .pro-tag{ margin-top:12px; color:var(--muted); font-size:var(--tag); }

        .layout{ display:grid; grid-template-columns: 1fr 400px; gap:24px; align-items:start; }
        @media (max-width:1100px){ .layout{ grid-template-columns:1fr; } }

        .pro-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:22px; }
        @media (max-width:1100px){ .pro-grid{ grid-template-columns:repeat(2,1fr); } }
        @media (max-width:720px){ .pro-grid{ grid-template-columns:1fr; } .pro-wrap{ padding:56px 20px 72px; } }

        .pro-card{
          background:#fff; border:1px solid #e6edf5; border-radius:16px; padding:22px 24px; overflow:hidden;
          transform: perspective(800px) rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg));
          transition: transform .15s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .pro-card:hover{ box-shadow:0 10px 30px rgba(2,6,23,.06); border-color:#dbe3ee; }
        .pro-shine{ position:absolute; inset:-1px; background:linear-gradient(120deg, transparent 35%, rgba(255,255,255,.6) 50%, transparent 65%); transform:translateX(-150%); transition:transform .6s ease; pointer-events:none; }
        .pro-card:hover .pro-shine{ transform:translateX(150%); }

        .pro-row{ display:flex; align-items:center; gap:14px; cursor:pointer; }
        .pro-icon{ width:80px; height:80px; flex:0 0 80px; border:0; border-radius:16px; background:#f5f7fb; display:inline-flex; align-items:center; justify-content:center; box-shadow:inset 0 0 0 1px #e6edf5; cursor:pointer; position:relative; }
        .pro-icon.press::after{ content:""; position:absolute; left:50%; top:50%; width:10px; height:10px; background:#00000014; border-radius:999px; transform:translate(-50%,-50%); animation:ripple .4s ease both; }
        @keyframes ripple{ from{ transform:translate(-50%,-50%) scale(.2); opacity:1 } to{ transform:translate(-50%,-50%) scale(6); opacity:0 } }
        .pro-emoji{ font-size:70px; line-height:1; }
        .pro-h3{ margin:0; font-size:1.15rem; font-weight:800; }
        .pro-desc{ margin-top:12px; color:var(--muted); font-size:.96rem; line-height:1.6; }

        /* color accents for titles + icon tints */
        .tone-green .pro-h3{ background:linear-gradient(90deg, var(--green), var(--greenDark)); -webkit-background-clip:text; background-clip:text; color:transparent; }
        .tone-green .pro-icon{ background:#f0fdf4; box-shadow:inset 0 0 0 1px #dcfce7; }
        .tone-blue  .pro-h3{ background:linear-gradient(90deg, var(--blue), var(--blueDark)); -webkit-background-clip:text; background-clip:text; color:transparent; }
        .tone-blue  .pro-icon{ background:#eff6ff; box-shadow:inset 0 0 0 1px #dbeafe; }

        /* entrance */
        .reveal{ opacity:0; transform:translateY(12px); }
        .is-mounted .reveal{ animation:rise .45s ease both; animation-delay:calc(var(--i) * 90ms); }
        @keyframes rise{ to{ opacity:1; transform:none } }

        /* sticky right panel (no random jumps) */
        .side{ position:sticky; top:24px; background:#fff; border:1px solid #e6edf5; border-radius:16px; padding:16px; min-height:360px; box-shadow:0 6px 18px rgba(2,6,23,.04); }
        .side-head{ display:flex; align-items:center; justify-content:space-between; }
        .side-title{ font-weight:800; }
        .side-close{ width:32px; height:32px; border:0; border-radius:8px; background:#f5f7fb; cursor:pointer; }
        .muted{ color:#64748b; } .small{ font-size:.9rem; }

        /* Ads preview */
        .ads-preview .ad-card{ border:1px solid #e6edf5; border-radius:14px; overflow:hidden; background:#fff; }
        .ad-media{ height:120px; display:flex; align-items:center; justify-content:center; background:#f3faf6; font-weight:800; }
        .ad-meta{ display:flex; align-items:center; justify-content:space-between; padding:12px; }
        .ad-title{ font-weight:700; } .ad-sub{ color:#94a3b8; font-size:.85rem; }
        .ad-stats{ display:flex; gap:10px; flex-wrap:wrap; margin-top:12px; }
        .ad-metrics{ display:flex; gap:12px; margin-top:8px; }

        .kpi{ background:#f5f7fb; border:1px solid #e6edf5; border-radius:12px; padding:10px 12px; } .kpi-v{ font-weight:800; } .kpi-l{ font-size:.85rem; color:#64748b; }
        .metric{ background:#f5f7fb; border:1px solid #e6edf5; border-radius:12px; padding:10px 12px; } .metric-v{ font-weight:800; } .metric-l{ font-size:.85rem; color:#64748b; }

        /* AI preview */
        .ai-rail{ position:relative; display:flex; align-items:center; gap:10px; margin:12px 0 16px; }
        .ai-rail .dot{ background:#1DB954; color:#fff; padding:6px 10px; border-radius:999px; font-size:.8rem; font-weight:700; }
        .ai-rail .dot.start{ background:#169c43; }
        .ai-rail .pipe{ flex:1; height:4px; background:#e9f7ef; border-radius:2px; position:relative; overflow:hidden; }
        .ai-rail .runner{ position:absolute; left:0; height:4px; width:18%; background:#1DB954; border-radius:2px; animation:run 2.2s ease-in-out infinite; }
        @keyframes run{ 0%{ left:0 } 50%{ left:82% } 100%{ left:0 } }
        .ai-bubbles{ list-style:none; padding:0; margin:0; display:grid; gap:8px; }
        .msg{ padding:10px 12px; border-radius:12px; background:#f5f7fb; max-width:80%; }
        .msg.in{ background:#eefbf4; } .msg.out{ margin-left:auto; background:#eaf2ff; }

        /* Analytics */
        .analytics .kpi-row{ display:flex; gap:12px; align-items:center; flex-wrap:wrap; margin:12px 0; }
        .block-title{ margin:12px 0 6px; font-weight:800; }
        .bars{ display:grid; gap:8px; }
        .bar-row{ display:grid; grid-template-columns:140px 1fr; gap:10px; align-items:center; }
        .bar-label{ font-weight:600; }
        .bar-track{ height:10px; background:#f1f5f9; border-radius:999px; overflow:hidden; border:1px solid #e6edf5; }
        .bar-fill.green{ height:100%; background:linear-gradient(90deg, #1DB954, #169c43); }
        .bar-fill.blue{  height:100%; background:linear-gradient(90deg, #0ea5e9, #2563eb); }

        /* Secure */
        .secure .check{ display:flex; align-items:center; gap:10px; margin:8px 0; }
        .secure .check input{ width:18px; height:18px; }
        .secure-result{ margin-top:10px; }

        /* Speed */
        .speed input[type="range"]{ width:100%; margin:6px 0 10px; }
        .meter{ height:10px; background:#f1f5f9; border-radius:999px; overflow:hidden; border:1px solid #e6edf5; }
        .meter-fill{ height:100%; background:linear-gradient(90deg, #1DB954, #169c43); transition: width .2s ease; }

        /* Commerce */
        .loader{ height:6px; background:linear-gradient(90deg, #e6f7ed, #1DB95433); border-radius:999px; position:relative; overflow:hidden; }
        .loader::after{ content:""; position:absolute; left:-30%; top:0; height:100%; width:30%; background:#1DB954; animation:load 1.2s infinite ease; border-radius:999px; }
        .loader.loader-blue{ background:linear-gradient(90deg, #e0f2fe, #0ea5e933); }
        .loader.loader-blue::after{ background:#0ea5e9; }
        @keyframes load{ 0%{ left:-30% } 100%{ left:100% } }

        .table-wrap{ overflow:auto; border:1px solid #e6edf5; border-radius:12px; margin-top:10px; }
        .tbl{ width:100%; border-collapse:collapse; font-size:.95rem; }
        .tbl th, .tbl td{ padding:10px 12px; border-top:1px solid #eef2f7; text-align:left; white-space:nowrap; }
        .tbl thead th{ background:#f7fafc; color:#475569; border-top:none; }

        /* Pills + Buttons */
        .pill{ padding:4px 8px; border-radius:999px; font-weight:700; font-size:.8rem; }
        .pill.paid{ background:#e8f9f0; color:#169c43; }
        .pill.pending{ background:#fff6e5; color:#b45309; }
        .pill.refunded{ background:#fee2e2; color:#b91c1c; }
        .pill.cod{ background:#eef2ff; color:#4338ca; }

        .btn{ border:0; border-radius:10px; padding:10px 12px; cursor:pointer; font-weight:700; }
        .btn-green{ background:#1DB954; color:#fff; } .btn-green:hover{ background:#169c43; }
        .btn-blue{  background:#0ea5e9; color:#fff; } .btn-blue:hover{  background:#2563eb; }
      `}</style>
    </section>
  );
};

export default Products;
